import { AuditPage } from './features/audit/AuditPage';
import Button from '@mui/material/Button';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Link, type RouteObject } from 'react-router';
import { RequireAuth } from './auth/RequireAuth';
import { AuthLayout } from './components/layout/AuthLayout';
import { AppLayout } from './components/layout/AppLayout';
import { useToast } from './components/Toast';
import { ChangePasswordForm } from './features/auth/ChangePasswordForm';
import { ForcedChangePasswordPage } from './features/auth/ForcedChangePasswordPage';
import { ForgotPasswordPage } from './features/auth/ForgotPasswordPage';
import { LoginPage } from './features/auth/LoginPage';
import { ResetPasswordPage } from './features/auth/ResetPasswordPage';
import { HomePage } from './features/home/HomePage';
import { RateSchedulesTab } from './features/settings/RateSchedulesTab';
import { TwoFactorSection } from './features/settings/TwoFactorSection';
import { AccountTab, SettingsPage } from './features/settings/SettingsPage';
import { UsersTab } from './features/users/UsersTab';
import { QrStickersTab } from './features/qrStickers/QrStickersTab';
import { ApiKeysTab } from './features/apiKeys/ApiKeysTab';
import { OrganisationsTab } from './features/organisations/OrganisationsTab';
import { SitesPage } from './features/chargers/SitesPage';
import { DriversPage } from './features/drivers/DriversPage';
import { JoinPage } from './features/drivers/JoinPage';
import { SessionsPage } from './features/sessions/SessionsPage';
import { ReportsPage } from './features/reports/ReportsPage';
import { PayToChargePage } from './features/payToCharge/PayToChargePage';

// Guests open this from a QR code on 4G: its own small download, loaded only here.
const ChargePage = lazy(() => import('./features/payToCharge/ChargePage').then((m) => ({ default: m.ChargePage })));

function AccountRoute() {
  const toast = useToast();
  return (
    <AccountTab>
      <ChangePasswordForm onDone={() => toast('Your password has been changed.')} />
      <TwoFactorSection />
    </AccountTab>
  );
}

function NotFound() {
  return (
    <AuthLayout title="This page doesn't exist." lead="The link may be old, or the address mistyped.">
      <Button component={Link} to="/" variant="contained">Go to Pulsepoint</Button>
    </AuthLayout>
  );
}

export const routes: RouteObject[] = [
  { path: '/sign-in', element: <LoginPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  // Drivers fill in their own details from an onboarding link: no sign-in.
  { path: '/join/:token', element: <JoinPage /> },
  // Pay to charge: what the QR stickers on chargers open (old sticker addresses included).
  { path: '/charge', element: <Suspense fallback={null}><ChargePage /></Suspense> },
  { path: '/charge/:code', element: <Suspense fallback={null}><ChargePage /></Suspense> },
  { path: '/change-password', element: <RequireAuth><ForcedChangePasswordPage /></RequireAuth> },
  {
    path: '/',
    element: <RequireAuth><AppLayout /></RequireAuth>,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'sites', element: <SitesPage /> },
      { path: 'sessions', element: <SessionsPage /> },
      { path: 'reports', element: <ReportsPage /> },
      { path: 'drivers', element: <DriversPage /> },
      { path: 'public-charging', element: <PayToChargePage /> },
      { path: 'audit', element: <AuditPage /> },
      {
        path: 'settings',
        element: <SettingsPage />,
        children: [
          { path: 'organisations', element: <OrganisationsTab /> },
          { path: 'peak-hours', element: <RateSchedulesTab /> },
          { path: 'people', element: <UsersTab /> },
          { path: 'qr-stickers', element: <QrStickersTab /> },
          { path: 'api-keys', element: <ApiKeysTab /> },
          { path: 'account', element: <AccountRoute /> },
        ],
      },
    ],
  },
  { path: '*', element: <NotFound /> },
];

export const router = createBrowserRouter(routes);
